import { FC, useCallback, useEffect, useState } from 'react';
import { Maps, Svg } from '@hopskipdrive/react-components';

import { useWebSocket } from '../../../../hooks/useWebSocket';

import { DriverMarker } from './CareDriver.styles';

interface CareDriver {
  rideCoordinates?: [number, number][];
  rideId: number;
  isInProgress: boolean;
  shouldHideCarMarker?: boolean;
}

type Message = {
  data: { lat: number; lng: number }[];
};

export const CareDriver: FC<CareDriver> = ({ rideCoordinates, rideId, isInProgress, shouldHideCarMarker }) => {
  const { lastJsonMessage: coordinatesMessage } = useWebSocket<Message>(
    {
      topic: 'coordinates',
      trip_id: rideId,
    },
    {},
    isInProgress
  );
  const [coordinatesOnMap, setCoordinatesOnMap] = useState<[number, number][]>(rideCoordinates || []);
  const [polylineInstance, setPolylineInstance] = useState<google.maps.Polyline>();
  const currentDriverPosition = coordinatesOnMap[coordinatesOnMap.length - 1];
  const prevDriverPosition = coordinatesOnMap[coordinatesOnMap.length - 2];

  const getMarkerRotation = (lastDriverPosition, driverPosition) => {
    // this is needed for pointing to the right direction
    const iconOffSetDeg = 255;
    const headingDirection = window.google.maps.geometry.spherical.computeHeading(lastDriverPosition, driverPosition);

    return headingDirection + iconOffSetDeg;
  };

  const getPosition = useCallback((LatLng) => {
    return new google.maps.LatLng(LatLng[0], LatLng[1]);
  }, []);

  const getPath = useCallback(() => {
    // this means a Path already exists, so do an incremental push instead
    if (polylineInstance) {
      const path = polylineInstance.getPath();

      path.push(new google.maps.LatLng(currentDriverPosition[0], currentDriverPosition[1]));

      return path;
    }

    return coordinatesOnMap.map((coordinate) => {
      return new google.maps.LatLng(coordinate[0], coordinate[1]);
    });
  }, [coordinatesOnMap, currentDriverPosition, polylineInstance]);

  useEffect(() => {
    // add incoming coordinates on the map
    if (coordinatesMessage && coordinatesMessage.data?.length) {
      setCoordinatesOnMap((prevCoords) =>
        prevCoords.concat([[coordinatesMessage.data[0].lat, coordinatesMessage.data[0].lng]])
      );
    }
  }, [coordinatesMessage]);

  if (!coordinatesOnMap.length) {
    return null;
  }

  return (
    <>
      {!shouldHideCarMarker ? (
        <Maps.Marker position={getPosition(currentDriverPosition)}>
          <DriverMarker
            data-testid="driver-marker"
            style={{
              transform: `rotate(${
                coordinatesOnMap.length > 1
                  ? getMarkerRotation(getPosition(prevDriverPosition), getPosition(currentDriverPosition))
                  : 0
              }deg)`,
            }}
          >
            <Svg icon="car" />
          </DriverMarker>
        </Maps.Marker>
      ) : null}

      <Maps.Polyline
        onLoad={setPolylineInstance}
        data-testid="driver-polyline"
        options={{
          path: getPath(),
          geodesic: true,
          strokeColor: '#1A4A7A',
          strokeOpacity: 0.8,
          strokeWeight: 3,
          zIndex: 2,
        }}
      />
    </>
  );
};
